/**
 * Password rules: a client-side mirror of the auth service's
 * validate_password_strength (api/rest/auth/src/auth/password_utils.py).
 *
 * Every page that sets a new password uses this, so the form never accepts
 * something the server then rejects. Keep the rules, their ORDER (the first
 * failing rule is the message shown, as on the server), the messages and the
 * special-character set identical to the server's.
 */

/** Exactly the server's set: other symbols (", ', /, \, ~, `) don't count. */
export const PASSWORD_SPECIAL_CHARACTERS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

export const PASSWORD_MIN_LENGTH = 8;
/** bcrypt ignores everything past 72 bytes; the server rejects longer. */
export const PASSWORD_MAX_BYTES = 72;

export interface PasswordRule {
  id: "length" | "maxBytes" | "upper" | "lower" | "number" | "special";
  /** Short checklist label. */
  label: string;
  /** The server's error message for this rule. */
  message: string;
  test: (password: string) => boolean;
  /** Shown in the checklist only once it fails (a limit, not a goal). */
  hiddenUntilFailed?: boolean;
}

const byteLength = (value: string): number => new TextEncoder().encode(value).length;

// Python's str.isupper()/islower()/isdigit() on one character, as regexes:
// isupper/islower are the Unicode Uppercase/Lowercase properties; isdigit is
// Numeric_Type Decimal or Digit, i.e. \p{Nd} plus the superscript, subscript,
// circled and other "digit" characters listed here (from Python's own table).
// Never stricter than the server, so a password the server takes is allowed.
export const UPPERCASE = /\p{Uppercase}/u;
export const LOWERCASE = /\p{Lowercase}/u;
export const DIGIT =
  /[\p{Nd}\u00B2-\u00B3\u00B9\u1369-\u1371\u19DA\u2070\u2074-\u2079\u2080-\u2089\u2460-\u2468\u2474-\u247C\u2488-\u2490\u24EA\u24F5-\u24FD\u24FF\u2776-\u277E\u2780-\u2788\u278A-\u2792\u{10A40}-\u{10A43}\u{10E60}-\u{10E68}\u{11052}-\u{1105A}\u{1F100}-\u{1F10A}]/u;

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    // Python len() counts code points, not UTF-16 units.
    test: (p) => Array.from(p).length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "maxBytes",
    label: "No longer than 72 bytes",
    message: "Password must be 72 bytes or less",
    test: (p) => byteLength(p) <= PASSWORD_MAX_BYTES,
    hiddenUntilFailed: true,
  },
  {
    id: "upper",
    label: "An uppercase letter (A-Z)",
    message: "Password must contain at least one uppercase letter",
    test: (p) => UPPERCASE.test(p),
  },
  {
    id: "lower",
    label: "A lowercase letter (a-z)",
    message: "Password must contain at least one lowercase letter",
    test: (p) => LOWERCASE.test(p),
  },
  {
    id: "number",
    label: "A number (0-9)",
    message: "Password must contain at least one number",
    test: (p) => DIGIT.test(p),
  },
  {
    id: "special",
    label: `A symbol: ${PASSWORD_SPECIAL_CHARACTERS}`,
    message: "Password must contain at least one special character",
    test: (p) => Array.from(p).some((c) => PASSWORD_SPECIAL_CHARACTERS.includes(c)),
  },
];

/** One-line summary of the rules, for helper text. */
export const PASSWORD_RULES_SUMMARY =
  "At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol";

/**
 * The server's verdict for this password: null when it would be accepted,
 * otherwise the same message the server would send (first failing rule).
 */
export function getPasswordError(password: string): string | null {
  const failed = PASSWORD_RULES.find((rule) => !rule.test(password));
  return failed ? failed.message : null;
}

export interface PasswordCheck {
  id: PasswordRule["id"];
  label: string;
  met: boolean;
}

/**
 * aria-describedby for a new-password field: its error text (MUI renders it
 * as `${fieldId}-helper-text`, only while there is one) and the checklist.
 * Setting aria-describedby by hand replaces MUI's own, so both go here.
 */
export function passwordFieldDescribedBy(
  fieldId: string,
  checklistId: string,
  hasHelperText: boolean,
): string {
  return hasHelperText ? `${fieldId}-helper-text ${checklistId}` : checklistId;
}

/** Live checklist state for a password being typed. */
export function getPasswordChecklist(password: string): PasswordCheck[] {
  return PASSWORD_RULES.filter(
    (rule) => !rule.hiddenUntilFailed || !rule.test(password),
  ).map((rule) => ({ id: rule.id, label: rule.label, met: rule.test(password) }));
}
